import {safeLocalStorage} from "@/lib/utils/storage";
import {normalizeSmsTag, readSmsTagTouches} from "@/lib/utils/smsTags";

/**
 * First-touch attribution (AC-711, spec §5–6; unblocks AC-6, 7, 8, 16).
 *
 * The partner link lands with the attribution in the query string, e.g.
 * `…/?traffic=7wj73kWxNSuMQr&ref=abc123&sub1=partner_data&utm_source=affiliate`.
 * Those params survive exactly one navigation — so they are captured on landing,
 * persisted, and replayed as Mixpanel super-properties on every subsequent event.
 * Without this, a deposit made three clicks later cannot be attributed to the
 * partner who sent the visitor.
 *
 * ─── First touch, not last ──────────────────────────────────────────────────
 * The first attributed landing wins and is never overwritten. A visitor who
 * arrives via a partner link, leaves, and comes back through a bookmark or an
 * organic search must still count for that partner — re-attributing them to
 * "organic" on the second visit would quietly delete the partner's conversion.
 * Consequently an *unattributed* landing persists nothing at all: it is not a
 * touch, so it cannot overwrite one.
 *
 * ─── Storage ────────────────────────────────────────────────────────────────
 * localStorage, following `smsTags.ts`: same `safeLocalStorage` wrapper (private
 * mode and disabled storage must never throw into a render), same
 * validate-then-persist shape, and a version stamp so a future change of meaning
 * can invalidate old records instead of silently mixing shapes. No new cookie —
 * this is read only in the browser, never by the server.
 *
 * ─── SMS campaign tag (AC-994) ──────────────────────────────────────────────
 * `traffic_source = "sms"` is first touch, so a visitor who first came from a
 * partner never gets it, and it does not say which campaign. The tag itself is
 * therefore sent too — `sms_tag` (last touch), `sms_tag_first` (first touch) and
 * `sms_tags` (all of them) — read from the SMS module's own record, not copied
 * into this one: `smsTags.ts` captures, validates and stores it.
 *
 * ─── Not yet resolvable ─────────────────────────────────────────────────────
 * `landing_id`, `partner_id` and `partner_code` (spec §5) have no frontend
 * source today — the redirector does not forward them. They are sent as explicit
 * `null` rather than omitted, so they exist in the Mixpanel Lexicon from day one
 * and the follow-up ticket that supplies real values needs no re-typing and no
 * backfill of the property definitions.
 */

/** localStorage key holding the first-touch attribution record (JSON). */
export const ATTRIBUTION_STORAGE_KEY = "attribution";

/**
 * Bumped only when the meaning of a stored field changes. A record with an
 * unrecognised version is treated as absent, so the next attributed landing
 * re-captures it.
 */
const ATTRIBUTION_VERSION = 1;

/** Spec §5 `traffic_source`. */
export type TrafficSource = "affiliate" | "sms" | "organic";

/** The attribution values that actually reach the frontend today. */
export interface AttributionRecord {
    v: number;
    /** Partner link hash — the `?traffic` param (`trafficHash` in the auth payloads). */
    partnerHash: string | null;
    /** Partner tracker click id — the `?ref` param (`clickHash` in the auth payloads). */
    clickId: string | null;
    /** Partner-defined passthrough value — the `?sub1` param. */
    sub1: string | null;
    campaignId: string | null;
    utmSource: string | null;
    utmMedium: string | null;
    utmCampaign: string | null;
    trafficSource: TrafficSource;
}

/**
 * The §5 attribution block as it goes to Mixpanel. Every key is always present —
 * a property that appears only on some events cannot be used as a funnel filter
 * without silently dropping the rest.
 *
 * A `type`, not an `interface`: only a type alias gets the implicit index
 * signature that makes it assignable to `registerSuperProperties`' bag.
 */
export type AttributionProperties = {
    traffic_source: TrafficSource;
    partner_hash: string | null;
    partner_id: string | null;
    partner_code: string | null;
    click_id: string | null;
    sub1: string | null;
    landing_id: string | null;
    campaign_id: string | null;
    utm_source: string | null;
    utm_medium: string | null;
    utm_campaign: string | null;
    /** AC-994: tag of the latest `?tag=` landing — last touch. */
    sms_tag: string | null;
    /** AC-994: tag of the first `?tag=` landing — first touch, never overwritten. */
    sms_tag_first: string | null;
    /** AC-994: every tag this browser landed with, oldest first. */
    sms_tags: string[] | null;
}

/**
 * Accepted shape of a raw attribution value. Deliberately narrow: these values
 * are pasted into a partner's URL by a third party and end up as Mixpanel
 * property values, so anything outside a conservative token charset — or longer
 * than a plausible id — is dropped rather than forwarded. Same posture as
 * `RAW_TAG_PATTERN` in `smsTags.ts`, widened only for the separators UTM tags
 * legitimately use.
 */
const ATTRIBUTION_VALUE_PATTERN = /^[a-zA-Z0-9_.:|-]{1,128}$/;

/** Trim + validate a raw query-param value; `null` when absent or malformed. */
const normalizeValue = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    const trimmed = raw.trim();
    return ATTRIBUTION_VALUE_PATTERN.test(trimmed) ? trimmed : null;
};

const isTrafficSource = (value: unknown): value is TrafficSource =>
    value === "affiliate" || value === "sms" || value === "organic";

const isAttributionRecord = (value: unknown): value is AttributionRecord => {
    if (typeof value !== "object" || value === null) return false;
    const record = value as Record<string, unknown>;
    if (record.v !== ATTRIBUTION_VERSION) return false;
    if (!isTrafficSource(record.trafficSource)) return false;

    const nullableStrings: (keyof AttributionRecord)[] = [
        "partnerHash",
        "clickId",
        "sub1",
        "campaignId",
        "utmSource",
        "utmMedium",
        "utmCampaign",
    ];
    return nullableStrings.every(
        (key) => record[key] === null || typeof record[key] === "string",
    );
};

/** The persisted first-touch record, or `null` if this visitor has never landed with attribution. */
export const readAttribution = (): AttributionRecord | null => {
    const stored = safeLocalStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!stored) return null;

    try {
        const parsed: unknown = JSON.parse(stored);
        return isAttributionRecord(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

/**
 * Parses the landing URL. Returns `null` when the URL carries no attribution
 * signal at all — an organic landing is not a touch and must not be persisted,
 * or it would shadow the partner touch that came before it.
 *
 * `?tag` (the SMS campaign param, already handled by `smsTags.ts`) counts as a
 * signal only for `traffic_source`: the tag itself is the SMS module's business
 * and is not duplicated into the attribution record (it reaches Mixpanel through
 * `getAttributionProperties`, from the SMS module's own storage).
 */
const parseAttribution = (params: URLSearchParams): AttributionRecord | null => {
    const partnerHash = normalizeValue(params.get("traffic"));
    const clickId = normalizeValue(params.get("ref"));
    const sub1 = normalizeValue(params.get("sub1"));
    const campaignId = normalizeValue(params.get("campaign_id"));
    const utmSource = normalizeValue(params.get("utm_source"));
    const utmMedium = normalizeValue(params.get("utm_medium"));
    const utmCampaign = normalizeValue(params.get("utm_campaign"));
    const isSms = normalizeSmsTag(params.get("tag")) !== null;

    const isAffiliate = !!partnerHash || !!clickId;
    const hasSignal =
        isAffiliate || isSms || !!sub1 || !!campaignId || !!utmSource || !!utmMedium || !!utmCampaign;
    if (!hasSignal) return null;

    return {
        v: ATTRIBUTION_VERSION,
        partnerHash,
        clickId,
        sub1,
        campaignId,
        utmSource,
        utmMedium,
        utmCampaign,
        // A partner link identifies itself by its hash/click id, whatever its UTM
        // tags claim; `?tag` marks the SMS channel. Everything else is organic —
        // a UTM-only landing carries its campaign in the `utm_*` properties.
        trafficSource: isAffiliate ? "affiliate" : isSms ? "sms" : "organic",
    };
};

/**
 * Capture on landing: parse the URL, and persist it as the first touch if — and
 * only if — it is attributed and nothing is stored yet. Idempotent, so it is
 * safe to call from an effect that re-runs on every navigation.
 *
 * Returns the record now in force (the stored first touch), or `null` for a
 * visitor who has never landed with attribution.
 */
export const captureAttribution = (params: URLSearchParams): AttributionRecord | null => {
    const existing = readAttribution();
    if (existing) return existing;

    const captured = parseAttribution(params);
    if (!captured) return null;

    safeLocalStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(captured));
    return captured;
};

/**
 * The §5 block (plus the SMS tag, AC-994) to register as Mixpanel
 * super-properties. Falls back to an all-null organic record so that every event
 * carries the full set of keys.
 */
export const getAttributionProperties = (): AttributionProperties => {
    const record = readAttribution();
    const smsTagTouches = readSmsTagTouches();

    return {
        traffic_source: record?.trafficSource ?? "organic",
        partner_hash: record?.partnerHash ?? null,
        click_id: record?.clickId ?? null,
        sub1: record?.sub1 ?? null,
        campaign_id: record?.campaignId ?? null,
        utm_source: record?.utmSource ?? null,
        utm_medium: record?.utmMedium ?? null,
        utm_campaign: record?.utmCampaign ?? null,
        sms_tag: smsTagTouches?.last ?? null,
        sms_tag_first: smsTagTouches?.first ?? null,
        sms_tags: smsTagTouches?.all.length ? smsTagTouches.all : null,
        // No frontend source yet — see the module header.
        partner_id: null,
        partner_code: null,
        landing_id: null,
    };
};
